/**
 * Analytics API client — fetches daily stats and by-shop stats from the backend (Snowflake).
 */
import type { ApiClient } from '../api/client.ts';

/**
 * The analytics shapes, computed server-side from live BETS (see apps/api analytics-daily.ts).
 * camelCase and already in dollars — the old uppercase/`DAILY_STATS` shapes are gone with the dead
 * pre-aggregated tables that produced them.
 */
export interface DailyStatsRow {
  readonly date: string;
  readonly totalBets: number;
  readonly activePlayers: number;
  readonly handle: number;
  readonly payout: number;
  readonly grossResult: number;
  readonly tax: number;
  readonly netResult: number;
  /** Fraction, e.g. 0.077 = 7.7%. */
  readonly hold: number;
  readonly mobileBets: number;
  readonly mobileHandle: number;
  readonly mobileGross: number;
  readonly retailBets: number;
  readonly retailHandle: number;
  readonly retailGross: number;
}

export interface ShopStatsRow {
  readonly date: string;
  readonly shop: string;
  readonly betsPlaced: number;
  readonly players: number;
  readonly handle: number;
  readonly payout: number;
  readonly grossResult: number;
}

export interface SnowflakeResult<T> {
  readonly columns: readonly string[];
  readonly rows: readonly T[];
  readonly rowCount: number;
}

export interface AnalyticsApi {
  readonly getDailyStats: (from: string, to: string) => Promise<SnowflakeResult<DailyStatsRow>>;
  readonly getShopStats: (
    from: string,
    to: string,
    shop?: string,
  ) => Promise<SnowflakeResult<ShopStatsRow>>;
  readonly getShops: () => Promise<readonly string[]>;
}

const passthrough = { safeParse: (v: unknown) => ({ success: true as const, data: v }) } as never;

export const createAnalyticsApi = (client: ApiClient): AnalyticsApi => ({
  getDailyStats: async (from, to) => {
    const result = await client.get(
      `/api/admin/analytics/daily?from=${from}&to=${to}`,
      passthrough,
    );
    if (!result.ok) throw new Error(result.failure.message);
    return result.data as unknown as SnowflakeResult<DailyStatsRow>;
  },

  getShopStats: async (from, to, shop) => {
    const params = new URLSearchParams({ from, to });
    if (shop) params.set('shop', shop);
    const result = await client.get(
      `/api/admin/analytics/daily-by-shop?${params.toString()}`,
      passthrough,
    );
    if (!result.ok) throw new Error(result.failure.message);
    return result.data as unknown as SnowflakeResult<ShopStatsRow>;
  },

  getShops: async () => {
    const result = await client.get('/api/admin/analytics/shops', passthrough);
    if (!result.ok) throw new Error(result.failure.message);
    return (result.data as unknown as { shops: string[] }).shops;
  },
});
